import { Check, Pencil, Trash2, X, AlertTriangle } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import { timeAgo } from "@/lib/time-ago"
import { formatDate, formatDateRange } from "@/lib/format-date"
import { CreatedByLine } from "@/components/group-features/CreatedByLine"

/** Single badge for table rows and detail header (pending + met + missed + overdue). */
export function ImperativeStatusBadge({ status, overdue }) {
  if (status === "met") {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
        <Check className="h-3 w-3 mr-1" />
        Met
      </Badge>
    )
  }
  if (status === "missed") {
    return (
      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
        <X className="h-3 w-3 mr-1" />
        Missed
      </Badge>
    )
  }
  if (overdue) {
    return (
      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Overdue
      </Badge>
    )
  }
  return (
    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
      Active
    </Badge>
  )
}

/**
 * Read-only detail + actions (mark met/missed, edit, delete). Used on the
 * imperatives index and the member dashboard Active Imperatives card.
 */
export function ImperativeDetailDialog({
  open,
  onOpenChange,
  imperative,
  currentUserId,
  isCoach = false,
  onEdit,
  onMarkMet,
  onMarkMissed,
  onDelete,
}) {
  if (!imperative) return null

  const canModify = imperative.canManage
  const isPending = imperative.status === "pending"
  const resolutionLabel = imperative.status === "met" ? "Met" : imperative.status === "missed" ? "Missed" : null

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <ResponsiveDialogTitle className="font-display text-xl">
              Imperative
            </ResponsiveDialogTitle>
            <ImperativeStatusBadge status={imperative.status} overdue={imperative.overdue} />
          </div>
          <ResponsiveDialogDescription>
            By {imperative.author.name} &middot; {timeAgo(imperative.createdAt)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-4 py-4 px-4 sm:px-0">
          <div>
            <p className="text-xs font-medium text-zinc-500 mb-1">Goal</p>
            <p className="text-sm text-zinc-900 whitespace-pre-wrap">{imperative.goal}</p>
          </div>

          {(imperative.startDate || imperative.endDate) && (
            <div>
              <p className="text-xs font-medium text-zinc-500 mb-1">Timeline</p>
              <p className="text-sm text-zinc-700">
                {formatDateRange(imperative.startDate, imperative.endDate)}
              </p>
            </div>
          )}

          {imperative.consequence && (
            <div>
              <p className="text-xs font-medium text-zinc-500 mb-1">Consequence</p>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap">{imperative.consequence}</p>
            </div>
          )}

          {resolutionLabel && imperative.resolvedAt && (
            <div className={`${imperative.status === "met" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border rounded-lg p-3`}>
              <p className={`text-xs font-medium ${imperative.status === "met" ? "text-green-700" : "text-red-700"}`}>
                {resolutionLabel} on {formatDate(imperative.resolvedAt?.split("T")[0])}
              </p>
            </div>
          )}

          <CreatedByLine author={imperative.author} createdBy={imperative.createdBy} />
        </div>

        {canModify && (
          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {isPending && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false)
                      onMarkMet(imperative)
                    }}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Mark Met
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      onOpenChange(false)
                      onMarkMissed(imperative)
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Mark Missed
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false)
                  onEdit(imperative)
                }}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => {
                  onOpenChange(false)
                  onDelete(imperative)
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </ResponsiveDialogFooter>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
